import { alpha } from '@mui/material/styles';
import { mobileAppGlassCard } from '../components/layout/mobile/mobileAppTheme';
import { THEME_COLORS } from '../shared/constants/theme';
import { isMobileShellLayout } from './mobileShellLayout';

/** Accent used across dashboard surfaces and primary actions */
export const DASHBOARD_ACCENT = '#4ce1b6';

/** Treat mobile shell as dark even when legacy theme flag is light. */
export function resolveDashboardThemeMode(themeMode) {
  return isMobileShellLayout() || themeMode === 'dark' ? 'dark' : 'light';
}

/** Shared empty / no-results card — dark glass on mobile shell, dark charcoal on desktop dark. */
export function getEmptyStateCardSx(themeMode, options = {}) {
  const { dashed = false, py, px, mb = 0 } = options;

  if (isMobileShellLayout()) {
    return {
      ...mobileAppGlassCard,
      textAlign: 'center',
      p: { xs: 3, sm: 4 },
      mb,
      ...(py !== undefined ? { py } : {}),
      ...(px !== undefined ? { px } : {}),
      ...(dashed ? { borderStyle: 'dashed' } : {}),
    };
  }

  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    textAlign: 'center',
    p: px !== undefined || py !== undefined ? undefined : 4,
    py: py ?? 4,
    px: px ?? 4,
    mb,
    borderRadius: 2,
    backgroundColor: isDark ? '#232329' : '#f5f5f5',
    border: dashed
      ? `1px dashed ${isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0'}`
      : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0e0e0'}`,
  };
}

export function getEmptyStateTextSx(themeMode, variant = 'body1') {
  if (isMobileShellLayout()) {
    return {
      color: 'rgba(148, 163, 184, 0.92)',
      fontSize: variant === 'body2' ? '0.875rem' : '0.95rem',
    };
  }

  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return {
    color: isDark ? 'rgba(148, 163, 184, 0.92)' : '#666',
    fontSize: variant === 'body2' ? '0.875rem' : '0.95rem',
  };
}

export function getEmptyStateTitleSx(themeMode) {
  if (isMobileShellLayout()) {
    return { color: '#f8fafc', fontWeight: 600 };
  }
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  return { color: isDark ? '#e2e8f0' : '#757575', fontWeight: 600 };
}

/**
 * MUI `sx` for dashboard Paper surfaces (controls strip + calendar card).
 * @param {'light'|'dark'} themeMode
 * @param {'controls'|'calendar'} variant
 * @returns {object} sx prop object
 */
export function getDashboardPaperSx(themeMode, variant = 'controls') {
  if (isMobileShellLayout()) {
    const sx = {
      ...mobileAppGlassCard,
      mb: variant === 'controls' ? 2 : 0,
    };
    if (variant === 'controls') {
      sx.p = { xs: 1.5, sm: 2 };
    } else if (variant === 'calendar') {
      sx.p = { xs: 1, sm: 1.5 };
      sx.overflow = 'hidden';
    }
    return sx;
  }

  const isDark = themeMode === 'dark';
  const sx = {
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    transition: 'background 0.3s ease, border-color 0.3s ease',
  };

  if (variant === 'controls') {
    sx.p = { xs: 2, sm: 3 };
    sx.mb = 3;
  } else if (variant === 'calendar') {
    sx.p = { xs: 1, sm: 2, md: 3 };
    sx.overflow = 'hidden';
  }

  return sx;
}

/** Dark/light surface tokens for section pages (distribution, configuration, stock). */
export function getDashboardDarkSurfaceTokens(isDark) {
  return {
    background: isDark ? THEME_COLORS.BACKGROUND_DARK : '#f2f4f7',
    surface: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
    surfaceGradient: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    surfaceBorder: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
    surfaceBorderStrong: isDark ? alpha('#ffffff', 0.12) : '#e0e0e0',
    headerBg: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : '#ffffff',
    chipBg: isDark ? alpha('#ffffff', 0.06) : '#f5f5f5',
    text: isDark ? '#e2e8f0' : '#212121',
    subtleText: isDark ? 'rgba(148, 163, 184, 0.92)' : '#757575',
  };
}

/** Desktop carousel tab card on distribution / configuration pages. */
export function getDashboardSectionTabCardSx(isDark, isActive, accent = DASHBOARD_ACCENT) {
  return {
    minWidth: 180,
    maxWidth: 180,
    p: 2.5,
    borderRadius: 2,
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : 'linear-gradient(165deg, #ffffff 0%, #f8fafc 100%)',
    border: `2px solid ${isActive ? accent : isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
    boxShadow: isActive
      ? isDark
        ? `0 12px 28px rgba(0,0,0,0.28), inset 0 0 0 1px ${alpha(accent, 0.14)}`
        : `0 8px 24px ${alpha(accent, 0.22)}`
      : isDark
        ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
        : 'none',
    '&:hover': {
      borderColor: accent,
      boxShadow: isDark
        ? `0 16px 32px rgba(0,0,0,0.34), inset 0 0 0 1px ${alpha(accent, 0.14)}`
        : `0 12px 24px ${alpha(accent, 0.18)}`,
      transform: 'translateY(-4px)',
    },
  };
}

/** Prev/next section navigation inside content panels. */
export function getDashboardSectionNavButtonSx(isDark, enabled, accent = DASHBOARD_ACCENT) {
  return {
    borderRadius: '10px',
    color: enabled ? accent : isDark ? alpha('#ffffff', 0.25) : '#ccc',
    backgroundColor: enabled ? alpha(accent, isDark ? 0.1 : 0.08) : 'transparent',
    border: `1px solid ${enabled ? alpha(accent, isDark ? 0.22 : 0.28) : isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.06)}`,
    '&:hover': {
      backgroundColor: enabled ? alpha(accent, isDark ? 0.18 : 0.12) : 'transparent',
      borderColor: enabled ? alpha(accent, isDark ? 0.32 : 0.4) : undefined,
    },
    '&:disabled': {
      color: isDark ? alpha('#ffffff', 0.25) : '#ccc',
      backgroundColor: 'transparent',
      borderColor: isDark ? alpha('#ffffff', 0.06) : alpha('#000', 0.06),
    },
    transition: 'all 0.3s ease',
  };
}

export function getDashboardSectionHeaderDividerSx(isDark) {
  return `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`;
}

/** Content panel below section tabs (distribution / configuration / stock). */
export function getDashboardSectionContentPaperSx(isDark, isMobile, options = {}) {
  const { p = isMobile ? { xs: 1.5, sm: 2 } : 4, minHeight = isMobile ? 0 : 400 } = options;

  if (isMobile) {
    return {
      ...mobileAppGlassCard,
      p,
      minHeight,
      bgcolor: 'transparent',
    };
  }

  return {
    p,
    minHeight,
    background: isDark
      ? `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`
      : '#ffffff',
    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
    borderRadius: 2,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
  };
}

/** Outer shell for dashboard routes (page background + padding). */
export function getDashboardPageShellSx(themeMode) {
  if (isMobileShellLayout()) {
    return {
      width: '100%',
      p: { xs: 1.5, sm: 2 },
      minHeight: 0,
    };
  }

  const isDark = themeMode === 'dark';
  return {
    p: { xs: 2, sm: 3 },
    backgroundColor: isDark ? THEME_COLORS.BACKGROUND_DARK : '#eef2f7',
    flex: 1,
    minHeight: 0,
    height: { xs: 'auto', lg: '100%' },
    maxHeight: { xs: 'none', lg: '100%' },
    display: 'flex',
    flexDirection: 'column',
    overflow: { xs: 'auto', lg: 'hidden' },
    boxSizing: 'border-box',
    transition: 'background-color 0.3s ease',
  };
}
